import type { Dictionary, Language } from "@/domain/dictionary";
import type { ExperimentalFeatures } from "@/domain/experimentalFeatures";
import type { Role } from "@/domain/permissions";
import type { DefaultUrlParams } from "@/domain/defaultUrlParams";
import type { AgeGroup, Arena, View } from "@/domain/types";
import { createAdminRequest, type AdminRequest } from "./adminRequest";
import { getSupabase } from "./supabaseClient";

/**
 * Client for the superadmin API (`functions/api/admin/*`). Those Pages
 * Functions re-verify the caller's token and role server-side and are the
 * only place the service-role key is used.
 */

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly validationError?: string,
  ) {
    super(message);
  }
}

export interface AdminUser {
  id: string;
  email: string;
  role: Role | null;
  location: string | null;
}

export interface AdminLocation {
  id: string;
  name: string;
  questionnaire_id: string;
  questionnaire_version: number;
  questionnaire_response_count: number;
}

export interface AdminApi {
  listUsers(): Promise<AdminUser[]>;
  inviteUser(input: { email: string; role: Role; location: string | null }): Promise<void>;
  updateUserRole(input: { userId: string; role: Role; location: string | null }): Promise<void>;
  deleteUser(userId: string): Promise<void>;
  listLocations(): Promise<AdminLocation[]>;
  createLocation(input: { id: string; name: string }): Promise<void>;
  deleteLocation(id: string): Promise<void>;
  setLocationQuestionnaire(input: {
    id: string;
    questionnaire_id: string;
    questionnaire_version: number;
  }): Promise<void>;
  deleteLocationResponses(id: string): Promise<{ location: string; deleted_count: number }>;
  saveAgeGroups(ageGroups: AgeGroup[]): Promise<AgeGroup[]>;
  saveArenas(arenas: Arena[]): Promise<Arena[]>;
  saveViews(views: View[]): Promise<{ views: View[]; dictionary: Dictionary }>;
  saveLanguages(
    languages: Language[],
    dictionary: Dictionary,
  ): Promise<{ languages: Language[]; dictionary: Dictionary }>;
  saveExperimentalFeatures(features: ExperimentalFeatures): Promise<void>;
  saveDefaultUrlParams(defaults: DefaultUrlParams): Promise<void>;
}

function createAdminApi(request: AdminRequest): AdminApi {
  async function call<T>(path: string, method: string, body?: unknown): Promise<T> {
    const response = await request(`/api/admin/${path}`, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
      validationError?: string;
    } & T;
    if (!response.ok) {
      throw new AdminApiError(
        payload.error ?? `HTTP ${response.status}`,
        response.status,
        payload.validationError,
      );
    }
    return payload;
  }

  return {
    listUsers: async () => (await call<{ users: AdminUser[] }>("users", "GET")).users,
    inviteUser: async (input) => void (await call("users", "POST", input)),
    updateUserRole: async (input) => void (await call("users", "PUT", input)),
    deleteUser: async (userId) => void (await call("users", "DELETE", { userId })),
    listLocations: async () =>
      (await call<{ locations: AdminLocation[] }>("locations", "GET")).locations,
    createLocation: async (input) => void (await call("locations", "POST", input)),
    deleteLocation: async (id) => void (await call("locations", "DELETE", { id })),
    setLocationQuestionnaire: async (input) => void (await call("locations", "PUT", input)),
    deleteLocationResponses: (id) => call("locations/responses", "POST", { id }),
    saveAgeGroups: async (ageGroups) =>
      (await call<{ ageGroups: AgeGroup[] }>("age-groups", "PUT", { ageGroups })).ageGroups,
    saveArenas: async (arenas) =>
      (await call<{ arenas: Arena[] }>("arenas", "PUT", { arenas })).arenas,
    saveViews: (views) => call("views", "PUT", { views }),
    saveLanguages: (languages, dictionary) => call("languages", "PUT", { languages, dictionary }),
    saveExperimentalFeatures: async (features) =>
      void (await call("experimental-features", "PUT", { features })),
    saveDefaultUrlParams: async (defaults) =>
      void (await call("default-url", "PUT", { defaults })),
  };
}

/** Admin API bound to the current Supabase session. */
export function createSessionAdminApi(sessionErrorMessage: () => string): AdminApi {
  const auth = getSupabase().auth;
  return createAdminApi(
    createAdminRequest(auth, (url, init) => fetch(url, init), sessionErrorMessage),
  );
}
